type FeedItem = Record<string, any> & { id?: string | number };

type LangEntry = {
  key: string;
  primary?: boolean;
  translations?: Record<string, any>;
};

/**
 * Merge a primary feed item with its translation override at the same slot.
 *
 * Slot identity comes from the array index. The override's `id` field then
 * tells us whether this slot got just a text overlay (same id as primary)
 * or a full replacement (different id = user picked a different product
 * for this language).
 *
 * - No override (null/undefined entry) → use primary
 * - override.id === primary.id → spread (overlay); missing fields fall back
 * - override.id !== primary.id → use override as-is (replacement); we don't
 *   pull primary fields in so we never mix data from two different products
 */
export function mergeItemAtSlot(
  primaryItem: FeedItem,
  override: FeedItem | null | undefined,
): FeedItem {
  if (!override) return primaryItem;
  if (
    override.id !== undefined &&
    primaryItem?.id !== undefined &&
    override.id !== primaryItem.id
  ) {
    return { ...override };
  }
  return { ...primaryItem, ...override };
}

/**
 * Resolve the displayed items for a feed block given the active language.
 *
 * Source of truth for primary items lives in `translations[primary][uid].items`
 * (same pattern as mj-button content / mj-rating questionText). Falls back to
 * `block.items` for legacy templates that don't carry primary translations
 * yet — those get seeded on first edit.
 *
 * Non-primary languages: `translations[lang][uid].items` is a sparse array
 * the same length as primary (entries can be null/undefined for "no
 * translation"). Each slot is matched by index, then by id (see
 * mergeItemAtSlot).
 */
export function getFeedItemsForLang(
  block: any,
  langs: LangEntry[] | undefined,
  langToShow: string | undefined,
): FeedItem[] {
  if (!block) return [];
  if (block.tagName !== "mj-feed-block") {
    return Array.isArray(block.items) ? block.items : [];
  }

  const primaryLang = langs?.find((l) => l.primary);
  const primaryFromTrans = primaryLang?.translations?.[block.uid]?.items;
  const primaryItems: FeedItem[] = Array.isArray(primaryFromTrans)
    ? primaryFromTrans
    : Array.isArray(block.items)
      ? block.items
      : [];

  if (!langToShow || !langs || langs.length === 0) {
    return primaryItems;
  }
  if (langToShow === primaryLang?.key) {
    return primaryItems;
  }
  const lang = langs.find((l) => l.key === langToShow);
  const overrideItems = lang?.translations?.[block.uid]?.items;
  if (!Array.isArray(overrideItems)) {
    return primaryItems;
  }
  return primaryItems.map((primaryItem: FeedItem, i: number) =>
    mergeItemAtSlot(primaryItem, overrideItems[i]),
  );
}
